import { axiosInstance, HttpStatusCode } from '@/plugins/axios';
import { authService, ROLES } from '@/services/auth';
import errorTracker from '@/plugins/errorTracker';
import { setUser } from '@sentry/browser';
import appConfig from '@/app.config.js';
import { showErrorToast, showSuccessToast } from '@/utils/toast';

export const moduleState = {
  user: null,
  roles: null,
  logins: null,
  isLoggedIn: false,
  authLoading: false,
  forceDeveloper: !!appConfig.forceDeveloper,
  authRolesOverrides: null,
};

export const mutations = {
  SET_CURRENT_USER(state, value) {
    state.user = value;
  },
  SET_CURRENT_ROLES(state, value) {
    state.roles = value;
  },
  SET_ALL_LOGINS(state, value) {
    state.logins = value;
  },
  SET_IS_LOGGED_IN(state, value) {
    state.isLoggedIn = value;
  },

  SET_LOADING(state, isLoading) {
    state.authLoading = isLoading;
  },
  SET_AUTH_ROLE_OVERRIDES(state, roles) {
    state.authRolesOverrides = roles;
  },
  RESET_AUTH_ROLE_OVERRIDES(state) {
    state.authRolesOverrides = null;
  },
};

export const getters = {
  userAccount: () => authService.getUser(),

  userEmail: state => state.user.email,

  isLoggedIn: state => state.isLoggedIn,

  authLoading: state => state.authLoading,

  userInRole: state => roleName => {
    if (!roleName) {
      console.error('userInRole: passed falsy rolename');
      return false;
    }
    const roles = state.authRolesOverrides ?? state.roles;
    return (
      Array.isArray(roles) && roles.some(role => role === roleName || role.displayName === roleName)
    );
  },

  userInRoles: state => roleNames => {
    if (roleNames.some(r => !r)) {
      console.error('userInRoles: rolenames contained at least one falsy member');
      return false;
    }
    const roles = state.authRolesOverrides ?? state.roles;
    return Array.isArray(roles) && roleNames.some(role => roles.includes(role));
  },

  isAdmin: (_, { userInRole }) => userInRole(ROLES.ADMIN),

  isAgentManager: (_, { userInRole }) => userInRole(ROLES.AGENT_MANAGER),

  isClientServices: (_, { userInRole }) => userInRole(ROLES.CLIENT_SERVICES),

  isClientServicesAdmin: (_, { userInRole }) => userInRole(ROLES.CLIENT_SERVICES_ADMIN),

  isCallCenterAdmin: (_, { userInRole }) => userInRole(ROLES.CALL_CENTER_ADMIN),

  isStaff: (_, { userInRole }) => userInRole(ROLES.STAFF),

  isProductAdmin: (_, { userInRole }) => userInRole(ROLES.PRODUCT_ADMIN),

  isProductViewer: (_, { userInRole }) => userInRole(ROLES.PRODUCT),

  isCancelSave: (_, { userInRole }) => userInRole(ROLES.CANCEL_SAVE),

  isSales: (_, { userInRole }) => userInRole(ROLES.SALES),

  isSalesManager: (_, { userInRole }) => userInRole(ROLES.SALES_MANAGER),

  isCancelSaveAdmin: (_, { userInRole }) => userInRole(ROLES.CANCEL_SAVE_ADMIN),

  isCorporateManager: (_, { userInRole }) => userInRole(ROLES.CORPORATE_MANAGER),

  isAccountCreator: (_, { userInRole }) => userInRole(ROLES.CORPORATE_BUCKET_ACCOUNT_CREATOR),

  isDeveloper: (state, { userInRole }) => userInRole(ROLES.DEVELOPER) || state.forceDeveloper,
};

export const actions = {
  // Called automatically when store initializes (see store/index.js)
  init() {
    // Clean up old localStorage entries that should no longer be persisted
    // These were security issues - auth data should always be fresh from API
    localStorage.removeItem('user');
    localStorage.removeItem('roles');
    localStorage.removeItem('logins');
  },

  resetSettings({ commit, dispatch }) {
    commit('SET_CURRENT_ROLES', null);
    commit('SET_CURRENT_USER', null);
    commit('SET_IS_LOGGED_IN', false);
    dispatch('reset');
  },

  reset() {
    authService.reset();
  },

  login() {
    authService.login();
  },

  loginAndRedirect(_, redirect) {
    authService.loginAndRedirect(redirect);
  },

  logout({ commit }) {
    authService.logout();
    commit('featureFlagStore/reset', null, { root: true });
  },

  async getToken() {
    return authService.getToken();
  },

  async getUser({ commit, dispatch }) {
    const adalUser = authService.getUser();

    if (!adalUser) {
      const error = new Error('Could not get user.');
      errorTracker.trackException({
        error,
      });
      dispatch('resetSettings');
      return null;
    }

    let email = '';

    if (adalUser.idToken.emails && adalUser.idToken.emails.length > 0) {
      email = adalUser.idToken.emails[0];
    }

    const response = await dispatch('request/post', {
      url: `/api/clients/auth`,
      errorMessage: 'Failed to authenticate client.',
    });

    if (!response) return null;

    const user = {
      name: adalUser.idToken.name,
      email,
      lastName: adalUser.idToken.family_name,
      firstName: adalUser.idToken.given_name,
      ...response.data,
    };

    commit('SET_CURRENT_ROLES', user.roles);
    commit('SET_CURRENT_USER', user);
    commit('SET_IS_LOGGED_IN', true);
    setUser({ email: user.email });

    return user;
  },

  async getAllLogins({ commit }, clientId) {
    commit('SET_LOADING', true);

    await axiosInstance
      .get(`/api/login/${clientId}/logins`)
      .then(response => {
        commit('SET_ALL_LOGINS', response.data);

        commit('SET_LOADING', false);

        return response.data;
      })
      .catch(err => {
        showErrorToast('Error: Network Error');
        console.error(err);

        commit('SET_LOADING', false);

        return Promise.reject(err);
      });
  },

  async setProperties(_, { data, b2cLoginId }) {
    try {
      await axiosInstance.put(`/api/login/${b2cLoginId}/properties`, data);
      showSuccessToast('Success: B2C account updated');
    } catch (err) {
      const { response } = err;
      if (response?.status === HttpStatusCode.BadRequest) {
        showErrorToast(response.data);
      } else {
        showErrorToast('Error: Unable to update B2C account');
      }
      console.error(err);
      throw err; // Re-throw so caller can handle it
    }
  },
};
